import { useState } from "react";
import { Loader2 } from "lucide-react";
import { isEmail, isWebsite, normalizeUrl } from "@/lib/validate";
import { useSubmission } from "@/hooks/useSubmission";
import { CTAButton } from "@/components/site/CTAButton";
import { ErrorAlert, Field, Honeypot, SuccessPanel, a11y, inputClass } from "@/components/forms/FormBits";

const EMPTY = { url: "", email: "", fax: "" };

const validate = (v) => {
  const e = {};
  if (!v.url.trim()) e.url = "Enter your website address.";
  else if (!isWebsite(v.url)) e.url = "That doesn't look like a website. Try something like yourbusiness.com.";
  if (!v.email.trim()) e.email = "Enter your email so we can send your review.";
  else if (!isEmail(v.email)) e.email = "Enter a valid email address, like you@business.com.";
  return e;
};

export const WebsiteCheckForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(null);
  const { status, error, submit, reset } = useSubmission("/website-check", {
    success: "Website check requested",
    successDescription: "We'll email your free review soon.",
  });

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const found = validate(values);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`check-${first}`)?.focus();
      return;
    }
    const payload = { url: normalizeUrl(values.url), email: values.email.trim(), fax: values.fax };
    if (await submit(payload)) {
      setSent(payload);
      setValues(EMPTY);
    }
  };

  if (status === "success" && sent) {
    return (
      <SuccessPanel
        testId="website-check-success"
        title="Got it. Your check is in the queue."
        body={`A founder will look over ${sent.url} by hand and email a short review of what's broken to ${sent.email}. You'll also get a quick confirmation email now.`}
        resetLabel="Check another website"
        onReset={() => {
          reset();
          setSent(null);
        }}
      />
    );
  }

  const busy = status === "submitting";
  return (
    <form noValidate onSubmit={onSubmit} className="relative space-y-5" data-testid="website-check-form">
      <Field id="check-url" label="Website address" required error={errors.url}>
        <input
          {...a11y("check-url", errors.url, true)}
          type="text"
          inputMode="url"
          autoComplete="url"
          placeholder="yourbusiness.com"
          value={values.url}
          onChange={set("url")}
          className={`${inputClass} h-12`}
          data-testid="website-check-url-input"
        />
      </Field>
      <Field id="check-email" label="Your email" required error={errors.email}>
        <input
          {...a11y("check-email", errors.email, true)}
          type="email"
          autoComplete="email"
          placeholder="you@business.com"
          value={values.email}
          onChange={set("email")}
          className={`${inputClass} h-12`}
          data-testid="website-check-email-input"
        />
      </Field>
      <Honeypot value={values.fax} onChange={set("fax")} />
      {status === "error" && <ErrorAlert message={error} testId="website-check-error" />}
      <CTAButton type="submit" size="lg" disabled={busy} arrow={!busy} className="w-full" testId="website-check-submit-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {busy ? "Sending…" : "Check my website"}
      </CTAButton>
      <p className="text-center text-xs text-[#A1A1AA]">Free, no obligation. We only use your email to send your review.</p>
    </form>
  );
};
